import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DataTable from './DataTable';
import { useAuth } from '../contexts/AuthContext';
import SearchableSelect from './SearchableSelect';
import FileUploadComponent from './FileUploadComponent';
import { FileUploadService } from '../services/FileUploadService';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import Loader from './Loader';
import RepairBatchEditModal from './RepairBatchEditModal';
import { formatDateForInputIST, formatDisplayDateIST, getTodayInputDateIST } from '../utils/dateUtils';
import ReceiptService from '../services/ReceiptService';
import { fetchAllPaginatedItems } from '../utils/paginationUtils';

const getDuplicateSerialIndexes = (serialNumbers = []) => {
  const seen = new Map();
  const duplicates = new Set();

  serialNumbers.forEach((serial, index) => {
    const normalized = (serial || '').trim().toLowerCase();
    if (!normalized) return;

    if (seen.has(normalized)) {
      duplicates.add(index);
      duplicates.add(seen.get(normalized));
      return;
    }

    seen.set(normalized, index);
  });

  return duplicates;
};

const extractSerialFromNotes = (notes) => {
  if (!notes) return '';
  const match = String(notes).match(/Serial:\s*([^|]+)/i);
  return match ? match[1].trim() : '';
};

const sanitizeRepairNotes = (notes) => {
  if (!notes) return '';

  return String(notes)
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part && !/^serial\s*:/i.test(part) && !/^batch\s*:/i.test(part))
    .join(' | ');
};

const getRepairFolderName = (assetId, repairId) => {
  if (!assetId || !repairId) return null;
  return `repair_${assetId}_${repairId}`;
};

const getInitialRepairBatchEditData = () => ({
  selectedRepairs: [],
  selectedProperties: [],
  propertyToAdd: '',
  repairDate: getTodayInputDateIST(),
  completedDate: '',
  repairReason: '',
  status: 'Pending',
  repairCost: 0,
  repairVendor: '',
  billNumber: '',
  notes: '',
  repairSource: 0
});

const getInitialRepairFormData = () => ({
  assetId: '',
  employeeId: '',
  repairDate: getTodayInputDateIST(),
  repairReason: '',
  status: 'Pending',
  repairCost: 0,
  repairVendor: '',
  billNumber: '',
  notes: '',
  repairSource: 0,
  attachmentFileName: '',
  completedDate: ''
});

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

function Repairs() {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canEditRepairs = hasPermission('canManageRepairs');
  const [repairs, setRepairs] = useState([]);
  const [repairGroups, setRepairGroups] = useState([]);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignableAssignedAssets, setAssignableAssignedAssets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetRepairs, setAssetRepairs] = useState([]);
  const [repairStats, setRepairStats] = useState({ all: 0, pending: 0, inRepair: 0, completed: 0, totalCost: 0, monthCost: 0 });
  const [assetRepairStats, setAssetRepairStats] = useState({ totalRepairs: 0, totalCost: 0 });
  const [statusFilter, setStatusFilter] = useState('All');
  const [groupByDate, setGroupByDate] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [groupedCurrentPage, setGroupedCurrentPage] = useState(1);
  const [groupedItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(25);
  const [totalRepairs, setTotalRepairs] = useState(0);
  const [totalRepairGroups, setTotalRepairGroups] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [batchEditData, setBatchEditData] = useState(getInitialRepairBatchEditData);
  const [copiedReasonKey, setCopiedReasonKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(getInitialRepairFormData);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [repairType, setRepairType] = useState('assigned'); // 'assigned' or 'office'
  const [officeRepairData, setOfficeRepairData] = useState({
    selectedAsset: null,
    repairQuantity: 1,
    serialNumbers: ['']
  });
  const [prefilledAssignments, setPrefilledAssignments] = useState([]);
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchRepairStats(),
        fetchAssignableAssignedAssets(),
        fetchAssets(),
        fetchEmployees(),
        fetchVendors(),
        fetchAssignments()
      ]);
    };
    document.title = 'Repairs - Saral Asset Manager';
    loadData();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setTablePage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (groupByDate) {
      fetchRepairGroups();
    } else {
      fetchRepairs();
    }
  }, [groupByDate, tablePage, tablePageSize, groupedCurrentPage, debouncedSearchTerm, statusFilter, sortField, sortDirection]);

  const openPrefilledAssignmentRepairModal = (incomingAssignments) => {
    const normalizedAssignments = (incomingAssignments || [])
      .map((assignment) => ({
        id: assignment.id ?? assignment.Id,
        assetId: assignment.assetId ?? assignment.AssetId,
        assetName: assignment.assetName || assignment.asset?.name || assignment.asset?.Name || 'Unknown Asset',
        employeeId: assignment.employeeId ?? assignment.EmployeeId ?? '',
        employeeName: assignment.employeeName || assignment.EmployeeName || assignment.employeeId || assignment.EmployeeId || 'Unknown',
        serialNumber: assignment.serialNumber || '',
        saralAssetId: assignment.saralAssetId || '',
        isActive: Boolean(assignment.isActive ?? assignment.IsActive)
      }))
      .filter((assignment) => assignment.assetId && assignment.employeeId);

    if (normalizedAssignments.length === 0) {
      return;
    }

    const primaryAssignment = normalizedAssignments[0];
    setEditingRepair(null);
    setSelectedFiles([]);
    setRepairType('assigned');
    setPrefilledAssignments(normalizedAssignments);
    setOfficeRepairData({
      selectedAsset: null,
      repairQuantity: 1,
      serialNumbers: ['']
    });
    setFormData({
      ...getInitialRepairFormData(),
      assetId: primaryAssignment.assetId,
      employeeId: primaryAssignment.employeeId,
      repairSource: primaryAssignment.isActive ? 1 : 2
    });
    setShowModal(true);
  };

  useEffect(() => {
    const incomingAssignments = location.state?.prefillRepairAssignments;
    if (Array.isArray(incomingAssignments) && incomingAssignments.length > 0) {
      openPrefilledAssignmentRepairModal(incomingAssignments);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const getRepairQueryParams = (page = tablePage, pageSize = tablePageSize, includeSearch = true) => ({
    page,
    pageSize,
    search: includeSearch && debouncedSearchTerm ? debouncedSearchTerm : undefined,
    status: statusFilter !== 'All' ? statusFilter : undefined,
    sortBy: sortField || undefined,
    sortDir: sortField ? sortDirection : undefined
  });

  const fetchRepairs = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/repairs/paged', {
        params: getRepairQueryParams()
      });

      setRepairs(response.data?.items || []);
      setTotalRepairs(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching repairs:', error);
      setRepairs([]);
      setTotalRepairs(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/repairs/grouped-by-date', {
        params: {
          page: groupedCurrentPage,
          pageSize: groupedItemsPerPage,
          search: debouncedSearchTerm || undefined,
          status: statusFilter !== 'All' ? statusFilter : undefined
        }
      });

      setRepairGroups(response.data?.items || []);
      setTotalRepairGroups(response.data?.total || 0);
      const validDateKeys = new Set((response.data?.items || []).map(group => group.dateKey));
      setExpandedDates(prev => new Set([...prev].filter(dateKey => validDateKeys.has(dateKey))));
    } catch (error) {
      console.error('Error fetching repair groups:', error);
      setRepairGroups([]);
      setTotalRepairGroups(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairStats = async () => {
    try {
      const response = await axios.get('/api/repairs/stats');
      const data = response.data || {};
      setRepairStats({
        all: data.all ?? data.totalRepairs ?? 0,
        pending: data.pending ?? 0,
        inRepair: data.inRepair ?? 0,
        completed: data.completed ?? 0,
        totalCost: data.totalCost ?? 0,
        monthCost: data.monthCost ?? 0
      });
    } catch (error) {
      console.error('Error fetching repair stats:', error);
      setRepairStats({ all: 0, pending: 0, inRepair: 0, completed: 0, totalCost: 0, monthCost: 0 });
    }
  };

  const fetchAssetRepairs = async (assetId) => {
    try {
      const [repairsResponse, statsResponse] = await Promise.all([
        axios.get(`/api/repairs/asset/${assetId}`),
        axios.get(`/api/repairs/asset/${assetId}/stats`)
      ]);
      setAssetRepairs(repairsResponse.data || []);
      const stats = statsResponse.data || {};
      setAssetRepairStats({
        totalRepairs: stats.totalRepairs ?? 0,
        totalCost: stats.totalCost ?? 0
      });
    } catch (error) {
      console.error('Error fetching asset repairs:', error);
      setAssetRepairs([]);
      setAssetRepairStats({ totalRepairs: 0, totalCost: 0 });
    }
  };

  const fetchAssignableAssignedAssets = async () => {
    try {
      const response = await axios.get('/api/repairs/assignable-assigned-assets');
      setAssignableAssignedAssets(response.data || []);
    } catch (error) {
      console.error('Error fetching assignable assigned assets:', error);
      setAssignableAssignedAssets([]);
    }
  };

  const handleViewHistory = (repair) => {
    const asset = repair.asset || repair.Asset;
    setSelectedAsset(asset);
    fetchAssetRepairs(repair.assetId || repair.AssetId);
    setShowHistoryModal(true);
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get('/api/assets');
      setAssets((response.data || []).filter(asset => asset.status !== 'Dead'));
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/api/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get('/api/assetassignments');
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const printRepairReceiptForRow = async (repair, mode = 'auto') => {
    const repairId = repair?.id ?? repair?.Id;
    const currentStatus = repair?.status || repair?.Status || 'Pending';
    const finalMode = mode === 'auto'
      ? (currentStatus === 'Completed' ? 'completion' : 'creation')
      : mode;

    if (!repairId) return;

    if (finalMode === 'completion') {
      await ReceiptService.generateRepairCompletionReceipt(repairId);
      return;
    }

    await ReceiptService.generateRepairCreationReceipt([repairId]);
  };

  const handleAssetChange = (assetId) => {
    // Handle both direct value and event object
    const actualAssetId = assetId?.target ? assetId.target.value : assetId;
    console.log('Selected asset ID:', actualAssetId);
    const activeAssignment = assignments.find(a => a.assetId == actualAssetId && a.isActive);
    if (activeAssignment) {
      setFormData({
        ...formData,
        assetId: actualAssetId,
        employeeId: activeAssignment.employeeId,
        repairSource: 1
      });
    } else {
      setFormData({
        ...formData,
        assetId: actualAssetId,
        employeeId: '',
        repairSource: 0
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const isPrefilledAssignmentCreate = !editingRepair && repairType === 'assigned' && prefilledAssignments.length > 0;

    if (repairType === 'office') {
      const duplicateSerialIndexes = getDuplicateSerialIndexes(officeRepairData.serialNumbers);
      if (duplicateSerialIndexes.size > 0) {
        showAlert('danger', 'Duplicate serial numbers are not allowed. Please use unique serial numbers.');
        return;
      }

      const selectedAssetId = officeRepairData.selectedAsset?.id;
      const enteredSerials = officeRepairData.serialNumbers
        .map((s) => (s || '').trim().toLowerCase())
        .filter(Boolean);
      const openStatuses = new Set(['pending', 'inrepair']);
      const assetRepairResponse = await axios.get(`/api/repairs/asset/${selectedAssetId}`);
      const existingOpenSerials = new Set(
        (assetRepairResponse.data || [])
          .filter((r) => {
            const repairId = r?.id ?? r?.Id;
            const assetId = r?.assetId ?? r?.AssetId;
            const status = (r?.status ?? r?.Status ?? '').toString().toLowerCase();
            return (
              String(assetId) === String(selectedAssetId) &&
              openStatuses.has(status) &&
              (!editingRepair || String(repairId) !== String(editingRepair.id || editingRepair.Id))
            );
          })
          .map((r) => extractSerialFromNotes(r?.notes ?? r?.Notes).toLowerCase())
          .filter(Boolean)
      );
      const conflictingSerials = enteredSerials.filter((s) => existingOpenSerials.has(s));
      if (conflictingSerials.length > 0) {
        const uniqueConflicts = [...new Set(conflictingSerials)];
        showAlert('danger', `Open repair already exists for serial: ${uniqueConflicts.join(', ')}`);
        return;
      }
    }

    setSaving(true);
    const previousStatus = editingRepair ? (editingRepair.status || editingRepair.Status || 'Pending') : null;

    const normalizeRepairPayload = (repairData) => {
      const payload = { ...repairData };
      // API DTO binding expects null for optional date fields, not empty strings.
      payload.repairDate = payload.repairDate || null;
      payload.completedDate = payload.completedDate || null;
      // Ensure numeric cost and remove UI-only helper fields.
      payload.repairCost = parseFloat(payload.repairCost) || 0;
      delete payload.attachmentFileNames;
      delete payload.serialNumber;
      return payload;
    };

    const uploadFilesForRepair = async (files, assetId, repairId) => {
      const folderName = getRepairFolderName(assetId, repairId);
      if (!folderName || files.length === 0) return '';

      try {
        return await FileUploadService.uploadFiles(files, folderName);
      } catch (uploadError) {
        console.error('File upload failed:', uploadError);
        return '';
      }
    };

    try {
      let fileNames = '';
      const createdRepairIds = [];

      if (isPrefilledAssignmentCreate) {
        for (const assignment of prefilledAssignments) {
          const assignmentNotes = [
            formData.notes,
            assignment.saralAssetId ? `Saral Asset ID: ${assignment.saralAssetId}` : '',
            assignment.serialNumber ? `Serial: ${assignment.serialNumber}` : ''
          ].filter(Boolean).join(' | ');

          const repairData = {
            ...formData,
            assetId: assignment.assetId,
            employeeId: assignment.employeeId,
            attachmentFileName: '',
            repairCost: parseFloat(formData.repairCost) || 0,
            repairSource: assignment.isActive ? formData.repairSource : 2,
            notes: assignmentNotes
          };

          const createResponse = await axios.post('/api/repairs', normalizeRepairPayload(repairData));
          const createdRepairId = createResponse?.data?.id ?? createResponse?.data?.Id;
          if (createdRepairId) {
            createdRepairIds.push(createdRepairId);
          }

          const individualFileNames = await uploadFilesForRepair(selectedFiles, assignment.assetId, createdRepairId);
          if (createdRepairId && individualFileNames) {
            await axios.put(`/api/repairs/${createdRepairId}`, normalizeRepairPayload({
              ...repairData,
              attachmentFileName: individualFileNames
            }));
          }
        }
      } else if (!editingRepair && repairType === 'office' && officeRepairData.selectedAsset && officeRepairData.serialNumbers.length > 1) {
        // Create multiple repair records for office repairs (one per serial).
        const batchId = `BATCH_${Date.now()}`;
        const costPerItem = parseFloat(formData.repairCost) || 0;
        
        for (let i = 0; i < officeRepairData.serialNumbers.length; i++) {
          const serialNumber = officeRepairData.serialNumbers[i];

          const repairData = {
            ...formData,
            assetId: officeRepairData.selectedAsset.id,
            employeeId: '',
            attachmentFileName: '',
            repairCost: costPerItem,
            notes: `${formData.notes}${formData.notes ? ' | ' : ''}Serial: ${serialNumber} | Batch: ${batchId}`,
            serialNumber
          };

          const createResponse = await axios.post('/api/repairs', normalizeRepairPayload(repairData));
          const createdRepairId = createResponse?.data?.id ?? createResponse?.data?.Id;
          if (createdRepairId) {
            createdRepairIds.push(createdRepairId);
          }
          const individualFileNames = await uploadFilesForRepair(selectedFiles, officeRepairData.selectedAsset.id, createdRepairId);

          if (createdRepairId && individualFileNames) {
            await axios.put(`/api/repairs/${createdRepairId}`, normalizeRepairPayload({
              ...repairData,
              attachmentFileName: individualFileNames
            }));
          }
        }
      } else {
        const assetId = repairType === 'office' ? officeRepairData.selectedAsset?.id : formData.assetId;

        if (selectedFiles.length > 0) {
          if (editingRepair) {
            fileNames = await uploadFilesForRepair(selectedFiles, assetId, editingRepair.id);
          }
        }

        const repairData = {
          ...formData,
          assetId,
          employeeId: repairType === 'office' ? '' : formData.employeeId,
          attachmentFileName: editingRepair
            ? [formData.attachmentFileName, fileNames].filter(Boolean).join(', ')
            : fileNames,
          repairCost: parseFloat(formData.repairCost) || 0,
          notes: repairType === 'office' && officeRepairData.serialNumbers[0]
            ? `${sanitizeRepairNotes(formData.notes)}${sanitizeRepairNotes(formData.notes) ? ' | ' : ''}Serial: ${officeRepairData.serialNumbers[0]}`
            : formData.notes
        };

        if (editingRepair) {
          await axios.put(`/api/repairs/${editingRepair.id}`, normalizeRepairPayload(repairData));
        } else {
          const createPayload = normalizeRepairPayload({
            ...repairData,
            attachmentFileName: ''
          });
          const createResponse = await axios.post('/api/repairs', createPayload);
          const createdRepairId = createResponse?.data?.id ?? createResponse?.data?.Id;
          if (createdRepairId) {
            createdRepairIds.push(createdRepairId);
          }

          if (selectedFiles.length > 0) {
            fileNames = await uploadFilesForRepair(selectedFiles, assetId, createdRepairId);
          }

          if (createdRepairId && fileNames) {
            await axios.put(`/api/repairs/${createdRepairId}`, normalizeRepairPayload({
              ...repairData,
              attachmentFileName: fileNames
            }));
          }
        }
      }

      if (!editingRepair && createdRepairIds.length > 0) {
        await ReceiptService.generateRepairCreationReceipt(createdRepairIds);
      } else if (editingRepair && formData.status === 'Completed' && previousStatus !== 'Completed') {
        await ReceiptService.generateRepairCompletionReceipt(editingRepair.id || editingRepair.Id);
      }
      
      await Promise.all([
        groupByDate ? fetchRepairGroups() : fetchRepairs(),
        fetchRepairStats(),
        fetchAssignableAssignedAssets()
      ]);
      handleCloseModal();
      showAlert(
        'success',
        editingRepair
          ? 'Repair updated successfully!'
          : createdRepairIds.length > 1
            ? `${createdRepairIds.length} repairs created successfully!`
            : 'Repair created successfully!'
      );
    } catch (error) {
      console.error('Error saving repair:', error);
      const errorMessage = error?.response?.data || error?.message || 'Error saving repair';
      showAlert('danger', String(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (repair) => {
    setEditingRepair(repair);
    const asset = repair.asset || repair.Asset;
    const repairSource = repair.repairSource ?? repair.RepairSource ?? 0;
    const existingAttachmentFileName = repair.attachmentFileName || repair.AttachmentFileName || '';
    setFormData({
      assetId: repair.assetId || repair.AssetId || '',
      employeeId: repair.employeeId || repair.EmployeeId || '',
      repairDate: formatDateForInputIST(repair.repairDate || repair.RepairDate) || getTodayInputDateIST(),
      repairReason: repair.repairReason || repair.RepairReason || '',
      status: repair.status || repair.Status || 'Pending',
      repairCost: repair.repairCost || repair.RepairCost || 0,
      repairVendor: repair.repairVendor || repair.RepairVendor || '',
      billNumber: repair.billNumber || repair.BillNumber || '',
      notes: sanitizeRepairNotes(repair.notes || repair.Notes || ''),
      repairSource,
      attachmentFileName: existingAttachmentFileName,
      completedDate: formatDateForInputIST(repair.completedDate || repair.CompletedDate) || ''
    });
    setSelectedFiles([]);
    setRepairType(repairSource === 0 ? 'office' : 'assigned');
    setOfficeRepairData({
      selectedAsset: repairSource === 0 && asset ? {
        id: asset.id || asset.Id,
        name: asset.name || asset.Name,
        quantity: asset.quantity || asset.Quantity || 1,
        assignedCount: asset.assignedCount || asset.AssignedCount || 0,
        deadCount: asset.deadCount || asset.DeadCount || 0
      } : null,
      repairQuantity: 1,
      serialNumbers: [extractSerialFromNotes(repair.notes || repair.Notes || '')]
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this repair record?')) {
      try {
        await axios.delete(`/api/repairs/${id}`);
        await Promise.all([
          groupByDate ? fetchRepairGroups() : fetchRepairs(),
          fetchRepairStats(),
          fetchAssignableAssignedAssets()
        ]);
        showAlert('success', 'Repair deleted successfully!');
      } catch (error) {
        console.error('Error deleting repair:', error);
        showAlert('danger', 'Error deleting repair');
      }
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`/api/repairs/${id}`)));
      setShowDeleteSelectedConfirm(false);
      setSelectedIds([]);
      showAlert('success', `${selectedIds.length} repair(s) deleted successfully!`);
      await Promise.all([
        groupByDate ? fetchRepairGroups() : fetchRepairs(),
        fetchRepairStats(),
        fetchAssignableAssignedAssets()
      ]);
    } catch (error) {
      console.error('Error deleting selected repairs:', error);
      showAlert('danger', 'Failed to delete selected repairs');
    }
  };

  const toggleSelectAll = () => {
    const currentPageIds = repairs.map(r => r.id || r.Id);
    const areAllCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));

    if (areAllCurrentPageSelected && selectedIds.length === totalRepairs) {
      setSelectedIds([]);
    } else {
      axios.get('/api/repairs/ids', {
        params: {
          search: debouncedSearchTerm || undefined,
          status: statusFilter !== 'All' ? statusFilter : undefined
        }
      }).then((response) => {
        setSelectedIds(response.data || []);
      }).catch((error) => {
        console.error('Error loading repair ids:', error);
        showAlert('danger', 'Failed to select all filtered repairs');
      });
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleClubbedRepairReceipt = async () => {
    let selectedRepairs = repairs.filter((repair) => selectedIds.includes(repair.id || repair.Id));
    if (selectedRepairs.length !== selectedIds.length) {
      const params = new URLSearchParams();
      selectedIds.forEach((id) => params.append('ids', id));
      const response = await axios.get('/api/repairs/by-ids', {
        params
      });
      selectedRepairs = response.data || [];
    }

    if (selectedRepairs.length < 2) {
      showAlert('danger', 'Select at least two repairs to generate a clubbed receipt.');
      return;
    }

    const statuses = [...new Set(selectedRepairs.map((repair) => getRepairStatus(repair)))];
    const hasCompleted = statuses.includes('Completed');
    const hasNonCompleted = statuses.some((status) => status !== 'Completed');

    if (hasCompleted && hasNonCompleted) {
      showAlert('danger', 'Please select either only completed repairs or only open/intake repairs for a clubbed receipt.');
      return;
    }

    try {
      const repairIds = selectedRepairs.map((repair) => repair.id || repair.Id);
      if (hasCompleted) {
        await ReceiptService.generateRepairCompletionReceiptBatch(repairIds);
      } else {
        await ReceiptService.generateRepairCreationReceipt(repairIds);
      }
    } catch (error) {
      console.error('Error generating clubbed repair receipt:', error);
      showAlert('danger', 'Failed to generate clubbed repair receipt.');
    }
  };

  const handleEditSelected = async () => {
    if (selectedIds.length < 2) return;

    setSaving(true);
    try {
      let selectedRepairs = repairs.filter((repair) => selectedIds.includes(repair.id || repair.Id));
      if (selectedRepairs.length !== selectedIds.length) {
        const params = new URLSearchParams();
        selectedIds.forEach((id) => params.append('ids', id));
        const response = await axios.get('/api/repairs/by-ids', { params });
        selectedRepairs = response.data || [];
      }

      if (selectedRepairs.length === 0) {
        showAlert('danger', 'No repairs were found for editing.');
        return;
      }

      const firstRepair = selectedRepairs[0];
      setBatchEditData({
        selectedRepairs: selectedRepairs.map((repair) => ({
          id: repair.id || repair.Id,
          assetId: repair.assetId || repair.AssetId || '',
          employeeId: repair.employeeId || repair.EmployeeId || '',
          attachmentFileName: repair.attachmentFileName || repair.AttachmentFileName || '',
          repairDate: formatDateForInputIST(repair.repairDate || repair.RepairDate) || '',
          completedDate: formatDateForInputIST(repair.completedDate || repair.CompletedDate) || '',
          repairReason: repair.repairReason || repair.RepairReason || '',
          status: repair.status || repair.Status || 'Pending',
          repairCost: repair.repairCost || repair.RepairCost || 0,
          repairVendor: repair.repairVendor || repair.RepairVendor || '',
          billNumber: repair.billNumber || repair.BillNumber || '',
          notes: sanitizeRepairNotes(repair.notes || repair.Notes || ''),
          repairSource: repair.repairSource ?? repair.RepairSource ?? 0
        })),
        selectedProperties: [],
        propertyToAdd: '',
        repairDate: formatDateForInputIST(firstRepair.repairDate || firstRepair.RepairDate) || getTodayInputDateIST(),
        completedDate: formatDateForInputIST(firstRepair.completedDate || firstRepair.CompletedDate) || '',
        repairReason: firstRepair.repairReason || firstRepair.RepairReason || '',
        status: firstRepair.status || firstRepair.Status || 'Pending',
        repairCost: firstRepair.repairCost || firstRepair.RepairCost || 0,
        repairVendor: firstRepair.repairVendor || firstRepair.RepairVendor || '',
        billNumber: firstRepair.billNumber || firstRepair.BillNumber || '',
        notes: sanitizeRepairNotes(firstRepair.notes || firstRepair.Notes || ''),
        repairSource: firstRepair.repairSource ?? firstRepair.RepairSource ?? 0
      });
      setShowBatchEditModal(true);
    } catch (error) {
      console.error('Error loading selected repairs for editing:', error);
      showAlert('danger', 'Failed to load selected repairs');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRepair(null);
    setSelectedFiles([]);
    setPrefilledAssignments([]);
    setRepairType('assigned');
    setOfficeRepairData({
      selectedAsset: null,
      repairQuantity: 1,
      serialNumbers: ['']
    });
    setFormData(getInitialRepairFormData());
  };

  const handleCloseBatchEditModal = () => {
    setShowBatchEditModal(false);
    setBatchEditData(getInitialRepairBatchEditData());
  };

  const handleBatchEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const selectedProperties = new Set(batchEditData.selectedProperties || []);
      const normalizeRepairPayload = (repairData) => {
        const payload = { ...repairData };
        payload.repairDate = payload.repairDate || null;
        payload.completedDate = payload.completedDate || null;
        payload.repairCost = parseFloat(payload.repairCost) || 0;
        return payload;
      };

      await Promise.all(batchEditData.selectedRepairs.map((repair) => {
        const payload = {
          assetId: repair.assetId,
          employeeId: repair.employeeId,
          repairDate: repair.repairDate || null,
          repairReason: repair.repairReason,
          status: repair.status,
          repairCost: repair.repairCost,
          repairVendor: repair.repairVendor,
          billNumber: repair.billNumber,
          notes: repair.notes,
          repairSource: repair.repairSource,
          attachmentFileName: repair.attachmentFileName,
          completedDate: repair.completedDate || null
        };

        if (selectedProperties.has('repairDate')) payload.repairDate = batchEditData.repairDate || null;
        if (selectedProperties.has('completedDate')) payload.completedDate = batchEditData.completedDate || null;
        if (selectedProperties.has('repairReason')) payload.repairReason = batchEditData.repairReason;
        if (selectedProperties.has('status')) payload.status = batchEditData.status;
        if (selectedProperties.has('repairCost')) payload.repairCost = batchEditData.repairCost;
        if (selectedProperties.has('repairVendor')) payload.repairVendor = batchEditData.repairVendor;
        if (selectedProperties.has('billNumber')) payload.billNumber = batchEditData.billNumber;
        if (selectedProperties.has('notes')) payload.notes = batchEditData.notes;
        if (selectedProperties.has('repairSource')) payload.repairSource = batchEditData.repairSource;

        return axios.put(`/api/repairs/${repair.id}`, normalizeRepairPayload(payload));
      }));

      handleCloseBatchEditModal();
      setSelectedIds([]);
      showAlert('success', `${batchEditData.selectedRepairs.length} repair(s) updated successfully!`);
      await Promise.all([
        groupByDate ? fetchRepairGroups() : fetchRepairs(),
        fetchRepairStats(),
        fetchAssignableAssignedAssets()
      ]);
    } catch (error) {
      console.error('Error updating selected repairs:', error);
      const errorMessage = error?.response?.data || error?.message || 'Failed to update selected repairs';
      showAlert('danger', String(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const getRepairDateValue = (repair) => repair?.repairDate || repair?.RepairDate;

  const getRepairDateKey = (repair) => {
    const dateValue = getRepairDateValue(repair);
    return formatDateForInputIST(dateValue) || 'Unknown Date';
  };

  const getRepairDateDisplay = (repair) => {
    const dateValue = getRepairDateValue(repair);
    return formatDisplayDateIST(dateValue);
  };

  const getDateKeyDisplay = (dateKey) => {
    if (dateKey === 'Unknown Date') return dateKey;
    return formatDisplayDateIST(dateKey);
  };

  const getRepairAssetLabel = (row) => {
    if (!row) return 'N/A';
    const asset = row.asset || row.Asset;
    if (!asset) return 'N/A';
    const name = asset.name || asset.Name || '';
    const assignment = assignments.find(a => a.assetId === asset.id && a.employeeId === row.employeeId);
    let serial = assignment?.serialNumber || 'No Serial';
    const notes = row.notes || row.Notes || '';
    const serialMatch = notes.match(/Serial: ([^|]+)/);
    if (serialMatch) {
      serial = serialMatch[1].trim();
    }
    const batchMatch = notes.match(/Batch: ([^|]+)/);
    const batchInfo = batchMatch ? ` (${batchMatch[1].trim()})` : '';
    return `${name} - ${serial}${batchInfo}`;
  };

  const getRepairEmployeeLabel = (row) => {
    if (!row) return '-';
    return row.employeeName || row.EmployeeName || row.employeeId || row.EmployeeId || '-';
  };

  const getRepairStatus = (row) => row?.status || row?.Status || 'Pending';

  const getStatusBadgeClass = (status) => (
    status === 'Dead' ? 'danger' : status === 'Completed' ? 'success' : status === 'InRepair' ? 'warning' : 'secondary'
  );

  const repairStatusOptions = [
    { value: 'All', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'InRepair', label: 'In Repair' },
    { value: 'Completed', label: 'Completed' }
  ];

  const hasActiveQuickFilters = statusFilter !== 'All';

  const quickFilterToolbar = (
    <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-2 h-100 w-100 grid-filter-toolbar grid-filter-toolbar-dense">
      <SearchableSelect
        className="grid-filter-select grid-filter-select-dense"
        controlClassName="form-control-sm"
        options={repairStatusOptions}
        value={statusFilter}
        placeholder="All Statuses"
        onChange={(e) => {
          setStatusFilter(e.target.value);
          setTablePage(1);
          setGroupedCurrentPage(1);
        }}
      />
      <button
        className="btn btn-sm btn-outline-secondary grid-filter-clear"
        onClick={() => {
          setStatusFilter('All');
          setTablePage(1);
        }}
        disabled={!hasActiveQuickFilters}
      >
        <i className="fas fa-eraser me-1"></i>Clear
      </button>
    </div>
  );

  const handleCopyReason = async (copyKey, text) => {
    if (!text || text === '-') return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedReasonKey(copyKey);
      window.setTimeout(() => {
        setCopiedReasonKey((current) => (current === copyKey ? null : current));
      }, 1500);
    } catch (error) {
      console.error('Error copying repair reason:', error);
    }
  };

  const columns = [
    { 
      field: 'checkbox', 
      label: (
        <input 
          type="checkbox" 
          checked={repairs.length > 0 && repairs.every(item => selectedIds.includes(item.id || item.Id))}
          onChange={toggleSelectAll}
        />
      ),
      render: (value, item) => (
        <input 
          type="checkbox" 
          checked={selectedIds.includes(item.id || item.Id)}
          onChange={() => toggleSelect(item.id || item.Id)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'id',
      label: 'ID',
      sortField: 'id',
      render: (value, row) => (
        <span className="repair-id-cell" title={String(value ?? row?.Id ?? '-')}>
          {value ?? row?.Id ?? '-'}
        </span>
      )
    },
    {
      field: 'asset',
      label: 'Asset',
      sortField: 'assetName',
      render: (value, row) => {
        const assetLabel = getRepairAssetLabel(row);
        return <span className="grid-ellipsis-text" title={assetLabel}>{assetLabel}</span>;
      }
    },
    { field: 'assetType', label: 'Asset Type', sortField: 'assetType', render: (value, row) => {
      const asset = row.asset || row.Asset;
      const assetType = asset?.assetType || asset?.AssetType;
      const typeLabel = assetType?.name || assetType?.Name || 'N/A';
      return <span className="grid-ellipsis-text" title={typeLabel}>{typeLabel}</span>;
    }},
    {
      field: 'employee',
      label: 'Employee',
      sortField: 'employeeName',
      render: (value, row) => {
        const employeeLabel = getRepairEmployeeLabel(row);
        return <span className="grid-ellipsis-text" title={employeeLabel}>{employeeLabel}</span>;
      }
    },
    {
      field: 'repairReason',
      label: 'Reason',
      sortField: 'repairReason',
      render: (value, row) => {
        const reason = row?.repairReason || row?.RepairReason || '-';
        const copyKey = `repair-reason-${row?.id || row?.Id}`;
        return (
          <div className="repair-reason-cell" title={reason}>
            <span className="repair-reason-text">{reason}</span>
            {reason !== '-' && (
              <button
                type="button"
                className="repair-reason-copy-button"
                onClick={() => handleCopyReason(copyKey, reason)}
                title={copiedReasonKey === copyKey ? 'Copied' : 'Copy full reason'}
                aria-label={copiedReasonKey === copyKey ? 'Copied' : 'Copy full reason'}
              >
                <i className={`fas ${copiedReasonKey === copyKey ? 'fa-check' : 'fa-copy'}`}></i>
              </button>
            )}
          </div>
        );
      }
    },
    { field: 'status', label: 'Status', sortField: 'status', render: (value, row) => {
      const status = getRepairStatus(row);
      return (
        <span className={`badge bg-${getStatusBadgeClass(status)}`}>
          {status}
        </span>
      );
    }},
    { field: 'repairCost', label: 'Cost', sortField: 'repairCost', render: (value, row) => {
      const cost = row?.repairCost || row?.RepairCost || 0;
      return `₹${parseFloat(cost).toFixed(2)}`;
    }},
    {
      field: 'repairVendor',
      label: 'Vendor',
      sortField: 'repairVendor',
      render: (value, row) => {
        const vendorLabel = row?.repairVendor || row?.RepairVendor || '-';
        return <span className="grid-ellipsis-text" title={vendorLabel}>{vendorLabel}</span>;
      }
    },
    {
      field: 'billNumber',
      label: 'Bill #',
      sortField: 'billNumber',
      render: (value, row) => {
        const billNumber = row?.billNumber || row?.BillNumber || '-';
        return <span className="grid-ellipsis-text" title={billNumber}>{billNumber}</span>;
      }
    },
    { field: 'attachment', label: 'Files', render: (value, row) => {
      const fileNames = row?.attachmentFileName || row?.AttachmentFileName;
      if (!fileNames) return '-';
      
      const folderName = `repair_${row.assetId}_${row.id}`;
      return (
        <button 
          className="btn btn-sm btn-outline-primary attachment-download-button"
          onClick={() => FileUploadService.downloadFiles(folderName)}
          title={`Download ${fileNames.split(',').length} file(s)`}
          aria-label={`Download ${fileNames.split(',').length} file(s)`}
        >
          <i className="fas fa-download"></i>
        </button>
      );
    }},
    {
      field: 'repairDate',
      label: 'Date',
      sortField: 'repairDate',
      cellClassName: 'grid-cell-nowrap',
      render: (value, row) => (
        <span className="grid-date-cell">{getRepairDateDisplay(row)}</span>
      )
    },
    { field: 'completedDate', label: 'Completed', sortField: 'completedDate', render: (value, row) => {
      const date = row?.completedDate || row?.CompletedDate;
      return <span className="grid-date-cell">{date ? formatDisplayDateIST(date) : '-'}</span>;
    }, cellClassName: 'grid-cell-nowrap'}
  ];

  const groupedDateKeys = repairGroups.map(group => group.dateKey);
  const areAllDateGroupsExpanded = groupedDateKeys.length > 0 && groupedDateKeys.every(dateKey => expandedDates.has(dateKey));

  useEffect(() => {
    if (!groupByDate) {
      setExpandedDates(new Set());
      setGroupedCurrentPage(1);
      return;
    }
  }, [groupByDate, statusFilter]);

  const toggleDateGroup = (dateKey) => {
    const nextExpanded = new Set(expandedDates);
    if (nextExpanded.has(dateKey)) {
      nextExpanded.delete(dateKey);
    } else {
      nextExpanded.add(dateKey);
    }
    setExpandedDates(nextExpanded);
  };

  const toggleAllDateGroups = () => {
    if (areAllDateGroupsExpanded) {
      setExpandedDates(new Set());
    } else {
      setExpandedDates(new Set(groupedDateKeys));
    }
  };

  const duplicateOfficeSerialIndexes = repairType === 'office'
    ? getDuplicateSerialIndexes(officeRepairData.serialNumbers)
    : new Set();
  const selectedRepairsInView = repairs.filter((repair) => selectedIds.includes(repair.id || repair.Id));
  const hasResolvedSelectedRepairs = selectedIds.length > 0 && selectedRepairsInView.length === selectedIds.length;
  const selectedRepairStatuses = [...new Set(selectedRepairsInView.map((repair) => getRepairStatus(repair)))];
  const canBulkEdit = !groupByDate && selectedIds.length > 1;
  const canClubReceipt = !groupByDate
    && selectedIds.length > 1
    && hasResolvedSelectedRepairs
    && selectedRepairStatuses.length === 1;
  const canDeleteSelected = !groupByDate && selectedIds.length > 0;
  const hasMultipleRowsSelected = !groupByDate && selectedIds.length > 1;
  const repairSummaryTiles = [
    { label: 'Total Repairs', value: repairStats.all, tone: 'primary' },
    { label: 'Pending', value: repairStats.pending, tone: 'warning' },
    { label: 'In Repair', value: repairStats.inRepair, tone: 'info' },
    { label: 'Completed', value: repairStats.completed, tone: 'success' },
    { label: 'Total Cost', value: formatCurrency(repairStats.totalCost), tone: 'secondary' },
    { label: 'This Month', value: formatCurrency(repairStats.monthCost), tone: 'dark' }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      <div className="page-header mb-4">
        <h1 className="page-title mb-0">Asset Repairs</h1>
        <div className="page-header-actions">
          {canEditRepairs && !groupByDate && selectedIds.length > 0 && (
            <>
              {canBulkEdit && (
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={handleEditSelected}
                >
                  <i className="fas fa-edit me-1"></i>Edit Selected ({selectedIds.length})
                </button>
              )}
              {canClubReceipt && (
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={handleClubbedRepairReceipt}
                >
                  <i className="fas fa-file-signature me-1"></i>Club Receipt ({selectedIds.length})
                </button>
              )}
              {canDeleteSelected && (
                <button 
                  className="btn btn-sm btn-danger me-2"
                  onClick={() => setShowDeleteSelectedConfirm(true)}
                >
                  <i className="fas fa-trash-alt me-1"></i>Delete Selected ({selectedIds.length})
                </button>
              )}
            </>
          )}
          {canEditRepairs && !hasMultipleRowsSelected && (
            <button
              className={`btn btn-sm ${groupByDate ? 'btn-primary' : 'btn-outline-primary'} me-2`}
              onClick={() => {
                setGroupByDate(!groupByDate);
                setSelectedIds([]);
                setTablePage(1);
                setGroupedCurrentPage(1);
              }}
            >
              <i className={`fas fa-${groupByDate ? 'calendar-alt' : 'list'} me-2`}></i>
              {groupByDate ? 'Grouped' : 'List'}
            </button>
          )}
          {canEditRepairs && !hasMultipleRowsSelected && (
            <button className="btn btn-primary" onClick={() => { setPrefilledAssignments([]); setShowModal(true); }}>
              <i className="fas fa-plus me-2"></i>Create Repair
            </button>
          )}
        </div>
      </div>

      <div className="row g-2 mb-3 app-summary-tiles">
        {repairSummaryTiles.map((tile) => (
          <div key={tile.label} className="col-6 col-md-4 col-xl-2">
            <div className={`card border-0 shadow-sm h-100 border-start border-4 border-${tile.tone}`}>
              <div className="card-body">
                <div className="text-muted small text-uppercase mb-2">{tile.label}</div>
                <div className="fs-4 fw-semibold">{tile.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card border-0 shadow-sm">
        <div className={groupByDate ? 'card-body p-0' : 'card-body'}>
          {groupByDate ? (
            loading ? (
              <div className="table-loader">
                <Loader text="Loading repairs..." size="medium" />
              </div>
            ) : (
            <div className="p-3">
              <div className="mb-3">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleAllDateGroups}
                >
                  <i className={`fas fa-${areAllDateGroupsExpanded ? 'minus' : 'plus'}-square me-1`}></i>
                  {areAllDateGroupsExpanded ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
              {(() => {
                if (repairGroups.length === 0) return <div className="text-center py-5"><p>No repairs</p></div>;

                const totalPages = Math.max(1, Math.ceil(totalRepairGroups / groupedItemsPerPage));
                const currentPage = Math.min(groupedCurrentPage, totalPages);

                return (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">
                        Showing {repairGroups.length} of {totalRepairGroups} dates
                      </small>
                      <small className="text-muted">Page {currentPage} / {totalPages}</small>
                    </div>

                    {repairGroups.map((group) => {
                      const dateKey = group.dateKey;
                      const dateRepairs = group.repairs || [];
                      const isExpanded = expandedDates.has(dateKey);
                      return (
                        <div key={dateKey} className="card mb-3 shadow-sm" style={{ border: '1px solid #dee2e6', overflow: 'visible' }}>
                          <div
                            className="card-header border-bottom d-flex justify-content-between align-items-center"
                            style={{ cursor: 'pointer', borderRadius: isExpanded ? '0.375rem 0.375rem 0 0' : '0.375rem' }}
                            onClick={() => toggleDateGroup(dateKey)}
                          >
                            <div>
                              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}></i>
                              <i className="fas fa-calendar me-2"></i>
                              <strong>{getDateKeyDisplay(dateKey)}</strong>
                            </div>
                            <span className="badge bg-primary text-white">{group.count || dateRepairs.length} repairs</span>
                          </div>

                          {isExpanded && (
                            <div className="card-body p-0" style={{ overflow: 'visible' }}>
                              <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>ID</th>
                                      <th>Asset</th>
                                      <th>Asset Type</th>
                                      <th>Employee</th>
                                      <th>Reason</th>
                                      <th>Status</th>
                                      <th>Cost</th>
                                      <th>Vendor</th>
                                      <th>Bill #</th>
                                      <th>Date</th>
                                      <th>Completed</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dateRepairs.map((repair) => {
                                      const status = getRepairStatus(repair);
                                      const asset = repair.asset || repair.Asset;
                                      const assetType = asset?.assetType || asset?.AssetType;
                                      const completedDate = repair.completedDate || repair.CompletedDate;
                                      return (
                                        <tr key={repair.id || repair.Id}>
                                          <td>{repair.id || repair.Id}</td>
                                          <td>{getRepairAssetLabel(repair)}</td>
                                          <td>{assetType?.name || assetType?.Name || 'N/A'}</td>
                                          <td>{getRepairEmployeeLabel(repair)}</td>
                                          <td>{repair.repairReason || repair.RepairReason || '-'}</td>
                                          <td>
                                            <span className={`badge bg-${getStatusBadgeClass(status)}`}>
                                              {status}
                                            </span>
                                          </td>
                                          <td>₹{parseFloat(repair.repairCost || repair.RepairCost || 0).toFixed(2)}</td>
                                          <td>{repair.repairVendor || repair.RepairVendor || '-'}</td>
                                          <td>{repair.billNumber || repair.BillNumber || '-'}</td>
                                          <td>{getRepairDateDisplay(repair)}</td>
                                          <td>{completedDate ? formatDisplayDateIST(completedDate) : '-'}</td>
                                          <td>
                                            <div className="dropdown">
                                              <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown">
                                                <i className="fas fa-ellipsis-v"></i>
                                              </button>
                                              <ul className="dropdown-menu dropdown-menu-end">
                                                <li>
                                                  <button className="dropdown-item" onClick={() => printRepairReceiptForRow(repair)}>
                                                    <i className="fas fa-file-invoice me-2"></i>Generate Receipt
                                                  </button>
                                                </li>
                                                <li>
                                                  <button className="dropdown-item" onClick={() => handleViewHistory(repair)}>
                                                    <i className="fas fa-history me-2"></i>View History
                                                  </button>
                                                </li>
                                                <li>
                                                  <button className="dropdown-item" onClick={() => handleEdit(repair)}>
                                                    <i className="fas fa-edit me-2"></i>Edit
                                                  </button>
                                                </li>
                                                {status !== 'Completed' && (
                                                  <>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    <li>
                                                      <button className="dropdown-item text-danger" onClick={() => handleDelete(repair.id || repair.Id)}>
                                                        <i className="fas fa-trash me-2"></i>Delete
                                                      </button>
                                                    </li>
                                                  </>
                                                )}
                                              </ul>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {totalPages > 1 && (
                      <nav>
                        <ul className="pagination justify-content-center mt-2">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const maxVisible = 5;
                            const halfVisible = Math.floor(maxVisible / 2);
                            let startPage = Math.max(1, currentPage - halfVisible);
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                            if (endPage - startPage < maxVisible - 1) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }

                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => setGroupedCurrentPage(i)}>{i}</button>
                                </li>
                              );
                            }
                            return pages;
                          })()}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setGroupedCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                );
              })()}
            </div>
            )
          ) : (
            <DataTable
              columns={columns}
              data={repairs}
              tableClassName="repairs-data-table"
              toolbarContent={quickFilterToolbar}
              serverSide
              loading={loading}
              page={tablePage}
              pageSize={tablePageSize}
              totalItems={totalRepairs}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={(field, direction) => {
                setSortField(field);
                setSortDirection(direction);
                setTablePage(1);
              }}
              onPageChange={setTablePage}
              getExportData={() => fetchAllPaginatedItems(async (page, pageSize) => {
                const response = await axios.get('/api/repairs/paged', {
                  params: getRepairQueryParams(page, pageSize, true)
                });

                return response.data;
              })}
              exportFileName="repairs"
              onEdit={canEditRepairs ? handleEdit : undefined}
              onDelete={canEditRepairs ? handleDelete : undefined}
              hideDelete={(repair) => getRepairStatus(repair) === 'Completed'}
              customActions={(repair) => (
                <>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => printRepairReceiptForRow(repair)}
                    >
                      <i className="fas fa-file-invoice me-2"></i>Generate Receipt
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleViewHistory(repair)}
                    >
                      <i className="fas fa-history me-2"></i>View History
                    </button>
                  </li>
                </>
              )}
            />
          )}
        </div>
      </div>

      {showHistoryModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-history me-2"></i>
                  Repair History - {selectedAsset?.name || selectedAsset?.Name} ({selectedAsset?.serialNumber || selectedAsset?.SerialNumber})
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <strong>Total Repairs:</strong> {assetRepairStats.totalRepairs} | 
                  <strong className="ms-3">Total Cost:</strong> ₹{Number(assetRepairStats.totalCost || 0).toFixed(2)}
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Reason</th>
                        <th>Status</th>
                        <th>Cost</th>
                        <th>Vendor</th>
                        <th>Bill #</th>
                        <th>Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetRepairs.map((repair, idx) => (
                        <tr key={idx}>
                          <td>{formatDisplayDateIST(repair.repairDate || repair.RepairDate)}</td>
                          <td>{repair.repairReason || repair.RepairReason}</td>
                          <td>
                            <span className={`badge bg-${(repair.status || repair.Status) === 'Completed' ? 'success' : (repair.status || repair.Status) === 'InRepair' ? 'warning' : 'secondary'}`}>
                              {repair.status || repair.Status}
                            </span>
                          </td>
                          <td>₹{(repair.repairCost || repair.RepairCost || 0).toFixed(2)}</td>
                          <td>{repair.repairVendor || repair.RepairVendor || '-'}</td>
                          <td>{repair.billNumber || repair.BillNumber || '-'}</td>
                          <td>
                            {getRepairEmployeeLabel(repair)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg repair-modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRepair ? 'Edit Repair' : prefilledAssignments.length > 1 ? 'Create Repairs' : 'Create Repair'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              
              {!editingRepair && prefilledAssignments.length === 0 && (
                <div className="modal-body border-bottom py-2">
                  <div className="d-flex justify-content-center">
                    <div className="btn-group" role="group">
                      <input 
                        type="radio" 
                        className="btn-check" 
                        name="repairType" 
                        id="assignedRepair" 
                        checked={repairType === 'assigned'}
                        onChange={() => setRepairType('assigned')}
                      />
                      <label className="btn btn-outline-primary" htmlFor="assignedRepair">
                        <i className="fas fa-user me-2"></i>Assigned Asset
                      </label>
                      
                      <input 
                        type="radio" 
                        className="btn-check" 
                        name="repairType" 
                        id="officeRepair" 
                        checked={repairType === 'office'}
                        onChange={() => {
                          setRepairType('office');
                          setFormData(prev => ({ ...prev, employeeId: '', assetId: '', repairSource: 0 }));
                        }}
                      />
                      <label className="btn btn-outline-primary" htmlFor="officeRepair">
                        <i className="fas fa-tools me-2"></i>Office Inventory
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {repairType === 'assigned' ? (
                    // Assigned Asset Repair Form
                    <div className="row">
                      {prefilledAssignments.length > 0 ? (
                        <div className="col-12 mb-3">
                          <div className="alert alert-light border mb-3">
                            <div className="fw-semibold mb-1">
                              {prefilledAssignments.length > 1
                                ? `${prefilledAssignments.length} assignments selected for repair creation`
                                : 'Creating repair for the selected assignment'}
                            </div>
                            <small className="text-muted">
                              Shared repair details below will be applied to each selected assignment.
                            </small>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm table-striped align-middle">
                              <thead>
                                <tr>
                                  <th>Asset</th>
                                  <th>Employee</th>
                                  <th>Saral Asset ID</th>
                                  <th>Serial Number</th>
                                </tr>
                              </thead>
                              <tbody>
                                {prefilledAssignments.map((assignment) => (
                                  <tr key={assignment.id}>
                                    <td>{assignment.assetName}</td>
                                    <td>{assignment.employeeName}</td>
                                    <td>{assignment.saralAssetId || '-'}</td>
                                    <td>{assignment.serialNumber || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Asset *</label>
                            <SearchableSelect
                              options={assignableAssignedAssets.map(item => ({
                                value: item.assetId,
                                label: `${item.assetName} - ${item.serialNumber || 'No Serial'}`
                              }))}
                              value={formData.assetId}
                              onChange={handleAssetChange}
                              placeholder="Select Assigned Asset"
                              required
                            />
                          </div>
                          {formData.repairSource !== 0 && (
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Employee *</label>
                              <SearchableSelect
                                options={employees.map(emp => ({ 
                                  value: emp.id, 
                                  label: emp.userName || emp.UserName || (emp.id ?? emp.Id)
                                }))}
                                value={formData.employeeId}
                                onChange={(value) => {
                                  const actualValue = value?.target ? value.target.value : value;
                                  setFormData({ ...formData, employeeId: actualValue });
                                }}
                                placeholder="Select Employee"
                                required
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    // Office Inventory Repair Form
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Asset *</label>
                        <SearchableSelect
                          options={assets.filter(asset => {
                            const available = asset.quantity - (asset.assignedCount || 0) - (asset.deadCount || 0);
                            return available > 0;
                          }).map(asset => ({
                            value: asset.id,
                            label: `${asset.name} (Available: ${asset.quantity - (asset.assignedCount || 0) - (asset.deadCount || 0)})`
                          }))}
                          value={officeRepairData.selectedAsset?.id || ''}
                          onChange={(e) => {
                            const asset = assets.find(a => a.id == e.target.value);
                            setOfficeRepairData({...officeRepairData, selectedAsset: asset, repairQuantity: 1, serialNumbers: ['']});
                          }}
                          placeholder="Select Asset from Inventory"
                          required
                        />
                      </div>
                      {officeRepairData.selectedAsset && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            max={officeRepairData.selectedAsset.quantity - (officeRepairData.selectedAsset.assignedCount || 0) - (officeRepairData.selectedAsset.deadCount || 0)}
                            className="form-control"
                            value={officeRepairData.repairQuantity}
                            onChange={(e) => {
                              const qty = parseInt(e.target.value) || 1;
                              if (qty > 0) {
                                const newSerials = Array(qty).fill('').map((_, i) => 
                                  officeRepairData.serialNumbers[i] || `${officeRepairData.selectedAsset.name}-${Date.now()}-${i + 1}`
                                );
                                setOfficeRepairData({...officeRepairData, repairQuantity: qty, serialNumbers: newSerials});
                              }
                            }}
                            required
                          />
                        </div>
                      )}
                      {officeRepairData.selectedAsset && officeRepairData.serialNumbers.length > 0 && (
                        <div className="col-12 mb-3">
                          <label className="form-label">Serial Numbers *</label>
                          {officeRepairData.repairQuantity > 1 && (
                            <div className="alert alert-info py-2 mb-2">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>Note:</strong> {officeRepairData.repairQuantity} separate repair records will be created for individual tracking.
                            </div>
                          )}
                          <div className="row g-2">
                            {officeRepairData.serialNumbers.map((serial, index) => (
                              <div key={index} className="col-md-4">
                                <input
                                  type="text"
                                  className={`form-control ${duplicateOfficeSerialIndexes.has(index) ? 'is-invalid' : ''}`}
                                  placeholder={`Serial ${index + 1}`}
                                  value={serial}
                                  onChange={(e) => {
                                    const newSerials = [...officeRepairData.serialNumbers];
                                    newSerials[index] = e.target.value;
                                    setOfficeRepairData({...officeRepairData, serialNumbers: newSerials});
                                  }}
                                  required
                                />
                              </div>
                            ))}
                          </div>
                          {duplicateOfficeSerialIndexes.size > 0 && (
                            <div className="text-danger small mt-2">
                              Duplicate serial numbers found. Each serial must be unique.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Common form fields for both repair types */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Repair Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.repairDate}
                        onChange={(e) => setFormData({ ...formData, repairDate: e.target.value })}
                        required
                      />
                    </div>

                    {editingRepair && (formData.status === 'Completed' || formData.status === 'Dead') && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Completion Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.completedDate}
                          onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
                        />
                      </div>
                    )}

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Repair Reason *</label>
                      <textarea
                        className="form-control"
                        value={formData.repairReason}
                        onChange={(e) => setFormData({ ...formData, repairReason: e.target.value })}
                        required
                        rows="2"
                      />
                    </div>

                    {editingRepair && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <SearchableSelect
                          options={[
                            { value: 'Pending', label: 'Pending' },
                            { value: 'InRepair', label: 'In Repair' },
                            { value: 'Completed', label: 'Completed' },
                            { value: 'Dead', label: 'Dead' }
                          ]}
                          value={formData.status}
                          onChange={(value) => {
                            const actualValue = value?.target ? value.target.value : value;
                            setFormData({ ...formData, status: actualValue });
                          }}
                          placeholder="Select Status"
                        />
                      </div>
                    )}

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Repair Cost (₹) - {repairType === 'office' && officeRepairData.repairQuantity > 1 ? 'Per item' : 'Total'}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.repairCost}
                        onChange={(e) => setFormData({ ...formData, repairCost: e.target.value })}
                        step="0.01"
                      />
                      {repairType === 'office' && officeRepairData.repairQuantity > 1 && (
                        <small className="text-muted">
                          Total cost: ₹{(parseFloat(formData.repairCost) * officeRepairData.repairQuantity || 0).toFixed(2)}
                        </small>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Repair Vendor</label>
                      <SearchableSelect
                        options={vendors.map(vendor => ({ value: vendor.name, label: vendor.name }))}
                        value={formData.repairVendor}
                        onChange={(value) => {
                          const actualValue = value?.target ? value.target.value : value;
                          setFormData({ ...formData, repairVendor: actualValue });
                        }}
                        placeholder="Select or add vendor"
                        onQuickAdd={async (name) => {
                          try {
                            const response = await axios.post('/api/vendors', {
                              name: name,
                              contactPerson: '',
                              phone: '',
                              email: '',
                              address: ''
                            });
                            await fetchVendors();
                            return { value: response.data.name, label: response.data.name };
                          } catch (error) {
                            console.error('Error adding vendor:', error);
                            throw error;
                          }
                        }}
                        addButtonText="Add Vendor"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bill Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.billNumber}
                        onChange={(e) => setFormData({ ...formData, billNumber: e.target.value })}
                      />
                    </div>

                    <FileUploadComponent
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      existingFiles={editingRepair && formData.attachmentFileName ? formData.attachmentFileName : null}
                      folderName={editingRepair ? getRepairFolderName(formData.assetId, editingRepair.id) : null}
                      label="Upload Bill/Receipt (Optional)"
                    />

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows="2"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Repair Initiated By</label>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={0}
                              checked={formData.repairSource === 0}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value), employeeId: '' })}
                            />
                            <label className="form-check-label">Office</label>
                            <small className="d-block text-muted">Office identified issue and sending for repair</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={1}
                              checked={formData.repairSource === 1}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value) })}
                            />
                            <label className="form-check-label">Employee Reported</label>
                            <small className="d-block text-muted">Employee reported issue, asset still with employee</small>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              name="repairSource"
                              value={2}
                              checked={formData.repairSource === 2}
                              onChange={(e) => setFormData({ ...formData, repairSource: parseInt(e.target.value) })}
                            />
                            <label className="form-check-label">Employee Returned</label>
                            <small className="d-block text-muted">Employee returned asset to office for repair</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                  <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                    {saving ? <Loader size="small" /> : (editingRepair ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <RepairBatchEditModal
        show={showBatchEditModal}
        saving={saving}
        batchEditData={batchEditData}
        setBatchEditData={setBatchEditData}
        onClose={handleCloseBatchEditModal}
        onSubmit={handleBatchEditSubmit}
      />

      <div className={`modal fade ${showDeleteSelectedConfirm ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteSelectedConfirm ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title"><i className="fas fa-exclamation-triangle me-2"></i>Confirm Delete Selected</h5>
              <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteSelectedConfirm(false)}></button>
            </div>
            <div className="modal-body">
              <p className="text-danger fw-bold">⚠️ WARNING: This will delete {selectedIds.length} selected repair(s) permanently!</p>
              <p>This action cannot be undone. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteSelectedConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected}>
                <i className="fas fa-trash-alt me-1"></i>Yes, Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Repairs;
